import { createContext, useState, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';

import { PREFERRED_UNIT_SYSTEMS, PreferredUnitSystem } from '@frontend/api';

import { getPreferredUnitSystem } from 'store/session/selectors';

type UnitSystemContextType = {
  preferredUnitSystem: PreferredUnitSystem;
  setPreferredUnitSystem: (unitSystem: PreferredUnitSystem) => void;
  togglePreferredUnitSystem: () => void;
  resetPreferredUnitSystem: () => void;
};

export const UnitSystemContext = createContext<
  UnitSystemContextType | undefined
>(undefined);

export const UnitSystemProvider = (props: React.PropsWithChildren<unknown>) => {
  const preferredUnitSystem: PreferredUnitSystem = useSelector(
    getPreferredUnitSystem,
  );
  const [unitSystem, setUnitSystem] =
    useState<PreferredUnitSystem>(preferredUnitSystem);

  const setPreferredUnitSystem = useCallback(
    (newUnitSystem: PreferredUnitSystem) => {
      setUnitSystem(newUnitSystem);
    },
    [],
  );

  const togglePreferredUnitSystem = useCallback(() => {
    setUnitSystem(
      unitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL
        ? PREFERRED_UNIT_SYSTEMS.METRIC
        : PREFERRED_UNIT_SYSTEMS.IMPERIAL,
    );
  }, [unitSystem]);

  const resetPreferredUnitSystem = useCallback(() => {
    setUnitSystem(preferredUnitSystem);
  }, [preferredUnitSystem]);

  return (
    <UnitSystemContext.Provider
      value={{
        preferredUnitSystem: unitSystem,
        setPreferredUnitSystem,
        togglePreferredUnitSystem,
        resetPreferredUnitSystem,
      }}
      {...props}
    />
  );
};

export const useUnitSystemContext = () => {
  const context = useContext(UnitSystemContext);
  if (!context) {
    throw new Error(
      'useUnitSystemContext must be used within a UnitSystemContext',
    );
  }
  return context;
};
